import { CalendarIcon } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

// Best-effort parse of an existing flight date string into a Date for the calendar.
// Handles ISO (2026-12-12), "12 Dec 2026", "12 Dec", "12DEC", "12DEC26".
function parseDateValue(v) {
  if (!v || typeof v !== "string") return undefined;
  const s = v.trim();
  const now = new Date();
  const fmts = ["yyyy-MM-dd", "d MMM yyyy", "d MMM", "ddMMMyy", "ddMMMyyyy", "ddMMM"];
  for (const f of fmts) {
    const d = parse(s, f, now);
    if (isValid(d)) return d;
  }
  return undefined;
}

export function DatePickerField({ value, onChange, placeholder = "Pick a date", testid }) {
  const selected = parseDateValue(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testid}
          className={`mt-0.5 w-full justify-start text-left font-normal h-9 border-slate-200 ${value ? "text-slate-900" : "text-slate-400"}`}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-slate-400" />
          <span className="truncate">{value || placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => { if (d) onChange(format(d, "d MMM yyyy")); }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// Convert an existing time string to 24h HH:MM for the native time selector.
// Handles "14:30", "0930", "600P", "6:00 PM", "9:20A".
function toHHMM(v) {
  if (!v || typeof v !== "string") return "";
  const s = v.trim().toUpperCase();
  let m = s.match(/^(\d{1,2}):(\d{2})\s*([AP])M?$/); // 6:00 PM
  if (m) return to24(m[1], m[2], m[3]);
  m = s.match(/^(\d{1,2})(\d{2})\s*([AP])M?$/); // 600P
  if (m) return to24(m[1], m[2], m[3]);
  m = s.match(/^(\d{1,2}):(\d{2})$/); // 14:30
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = s.match(/^(\d{1,2})(\d{2})$/); // 1430 / 930
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return "";
}

function to24(h, min, ap) {
  let hh = parseInt(h, 10) % 12;
  if (ap === "P") hh += 12;
  return `${String(hh).padStart(2, "0")}:${min}`;
}

export function TimePickerField({ value, onChange, testid }) {
  return (
    <Input
      type="time"
      value={toHHMM(value)}
      onChange={(e) => onChange(e.target.value)}
      className="mt-0.5 h-9 font-pnr"
      data-testid={testid}
    />
  );
}
